import { MapContainer, TileLayer } from 'react-leaflet';
import type { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import type { Metro } from '../../types/metro';
import { MetroBubble } from './MetroBubble';
import { CARTO_TILE_REFERRER_POLICY, CARTO_TILE_SUBDOMAINS, LIGHT_TILE_URL } from '../../lib/tile-url';

/**
 * Small Alaska + Hawaii inset maps rendered in the bottom-right corner
 * of the main map. Standard treatment on US thematic maps — the
 * contiguous 48 fit the primary map viewport comfortably, and the
 * two non-contiguous states get inset boxes so no state is visually
 * dropped from the country.
 *
 * 2026-07-03 (V2 outstanding item): Georgetown reviewers flagged
 * that AK/HI were absent from the map. The 55-metro dataset doesn't
 * currently include any AK/HI metros, so the insets will render as
 * empty tile boxes until Georgetown decides to add coverage. The
 * insets stay visible regardless so the map reads as "all 50 states
 * are represented in the framing," not "we quietly excluded two."
 *
 * 2026-07-08 (CR-12, Maria Slack 3d): repositioned from bottom-left
 * to bottom-right. `MapSidebar` occupies the FULL left edge height
 * (`absolute bottom-4 left-4 top-4 w-[288px] z-[400]`), so the old
 * `bottom-4 left-4` placement sat directly on top of the sidebar's
 * bottom edge and — because the combined inset width (344px) is
 * wider than the sidebar — spilled past it onto the map, hiding
 * west-coast metros underneath. `bottom-4 right-4` is clear of the
 * sidebar (left), `MapLegend` (now top-center, see MapLegend.tsx),
 * and `MapZoom` (top-right) — no other floating chrome claims the
 * bottom-right quadrant.
 *
 * 2026-07-08 (CR-26, design-review follow-up): the bottom-right pair
 * was still laid out as a horizontal row (~344px wide), so its left
 * box sat over the Southeast — hiding the Florida metros (Miami,
 * Tampa) under the AK inset. Fix: STACK the two insets VERTICALLY and
 * SHRINK them (132×84 each) so the footprint becomes a thin column
 * pinned to the far-right edge (~132px wide), sitting over the
 * Atlantic east of Florida instead of on top of it. Both AK + HI stay
 * visible (never only one). Kept small deliberately — the dataset has
 * no AK/HI metros, so the insets are a lightweight "all 50 states are
 * framed" reference, not a data surface.
 *
 * Positioning: `absolute bottom-4 right-4 z-[500]`. Two mini
 * MapContainers stacked vertically (AK on top, HI below), each
 * ~132×84px. Each carries its own bounded view via `bounds`
 * (fitBounds equivalent on mount) so the state fills the inset
 * without letting the user pan away.
 *
 * Bubble rendering: each inset reuses the same `MetroBubble`
 * component the main map uses. Bubbles here would inherit the
 * ratio-driven color + size and the OG-lens behavior. Since no
 * metros currently fall inside these bounds, the loop is a no-op
 * for now — but the wiring is ready.
 */

const AK_BOUNDS: LatLngBoundsExpression = [
  [51, -170], // SW
  [72, -130], // NE (Aleutians wrap eastwards; -170 covers the tail)
];
const AK_CENTER: LatLngExpression = [64, -150];

const HI_BOUNDS: LatLngBoundsExpression = [
  [18.9, -161], // SW (Ni'ihau)
  [22.5, -154], // NE (Big Island's northeast tip)
];
const HI_CENTER: LatLngExpression = [20.5, -157];

interface MapInsetsProps {
  readonly metros: ReadonlyArray<Metro>;
}

function isInBounds(
  metro: Metro,
  bounds: readonly [readonly [number, number], readonly [number, number]],
): boolean {
  const [[sLat, wLng], [nLat, eLng]] = bounds;
  return metro.lat >= sLat && metro.lat <= nLat && metro.lng >= wLng && metro.lng <= eLng;
}

const AK_BOUNDS_TUPLE: readonly [readonly [number, number], readonly [number, number]] = [
  [51, -170],
  [72, -130],
];
const HI_BOUNDS_TUPLE: readonly [readonly [number, number], readonly [number, number]] = [
  [18.9, -161],
  [22.5, -154],
];

export function MapInsets({ metros }: MapInsetsProps) {
  const akMetros = metros.filter((m) => isInBounds(m, AK_BOUNDS_TUPLE));
  const hiMetros = metros.filter((m) => isInBounds(m, HI_BOUNDS_TUPLE));
  return (
    <div
      // A11y (WCAG 4.1.2): a plain <div> has an implicit `generic` role, which
      // PROHIBITS aria-label (axe `aria-prohibited-attr`). These two insets are
      // a labelled group, so role="group" is both correct and lets the
      // accessible name stand.
      role="group"
      aria-label="Alaska and Hawaii insets"
      // Positioned relative to the map container. The map itself is
      // `overflow-hidden` so the insets clip cleanly to the map
      // viewport if the user resizes the window narrower than the
      // insets' combined width. Bottom-right (CR-12): clear of the
      // full-height left sidebar and the top-center legend.
      className="absolute bottom-4 right-4 z-[500] hidden flex-col gap-1 md:flex"
    >
      {/* CR-26: vertical stack (AK over HI) + smaller cards → a thin
          far-right column that clears the Florida metros the old
          horizontal row covered. */}
      <div className="flex flex-col gap-1.5">
        <InsetCard
          label="AK"
          center={AK_CENTER}
          bounds={AK_BOUNDS}
          metros={akMetros}
        />
        <InsetCard
          label="HI"
          center={HI_CENTER}
          bounds={HI_BOUNDS}
          metros={hiMetros}
        />
      </div>
    </div>
  );
}

interface InsetCardProps {
  readonly label: string;
  readonly center: LatLngExpression;
  readonly bounds: LatLngBoundsExpression;
  readonly metros: ReadonlyArray<Metro>;
}

function InsetCard({ label, center, bounds, metros }: InsetCardProps) {
  return (
    <div
      className="relative overflow-hidden rounded-md border border-border bg-white shadow-panel"
      style={{ width: 132, height: 84 }}
    >
      <span
        aria-hidden="true"
        className="absolute left-1.5 top-1 z-[1] rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy"
      >
        {label}
      </span>
      <MapContainer
        center={center}
        bounds={bounds}
        boundsOptions={{ padding: [4, 4] }}
        // Insets are strictly reference views -- the user can't zoom
        // or pan them. Locks the camera so a stray scroll doesn't
        // send the inset off to open ocean.
        zoomControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        keyboard={false}
        attributionControl={false}
        // 100% of the parent -- the parent controls the inset size.
        style={{ width: '100%', height: '100%' }}
      >
        {/* CR-033: URL (incl. CARTO key) owned by lib/tile-url.ts.
            CR-040: per-tile referrerPolicy so the domain-restricted key works. */}
        <TileLayer
          url={LIGHT_TILE_URL}
          subdomains={CARTO_TILE_SUBDOMAINS}
          maxZoom={19}
          referrerPolicy={CARTO_TILE_REFERRER_POLICY}
        />
        {metros.map((m) => (
          <MetroBubble
            key={m.id}
            metro={m}
            isSelected={false}
            onSelect={() => {
              /* Insets are read-only. Click handling stays on the
               * main map so the sidebar stays the single source of
               * navigation. */
            }}
          />
        ))}
      </MapContainer>
    </div>
  );
}
